import { Store } from 'vuex';
import { RsModule } from './types/rs-module';
import { TypedModule } from './types/typed-module';

/**
 * Creates a strongly typed module wrapper around a vuex store module. This typed module wrapper uses inference from a
 * module definition. In order for this to work most effectively, the origin module should be well typed.
 *
 * @param store - A reference to the central store the module is attached to.
 * @param moduleName - A name to use for the module as a key... Required for vuex to do its thing under the hood.
 * @param _module - A reference to the module definition.
 * @returns A strongly typed module object.
 */
export const generateTypedModule = <
  TState extends object,
  TModule extends RsModule<TState> = RsModule<TState>
>(
  store: Store<any>,
  moduleName: string,
  _module: TModule,
): TypedModule<TModule['state'], TModule> => ({
  /**
   * Access module state as Readonly data.
   * State data must be accessed as function calls to their respective keys.
   * Ex: `someModule.state.hello()`
  */
  state: Object.keys(typeof _module.state !== 'function' ? _module.state! : (_module.state as (() => TState))()).reduce((acc, key) => {
    const keyOf = key as keyof typeof _module['state'];
    const state = typeof _module.state !== 'function' ? _module.state! : (_module.state as (() => TState))();
    return {
      ...acc,
      [key]: () => state[keyOf] as Readonly<typeof state[typeof keyOf]>,
    };
  }, {} as TypedModule<TState, TModule>['state']),
  /**
   * Access module getters.
   * Getters must be accessed as function calls to their respective keys.
   * Ex: `someModule.getters.hello()`
  */
  getters: Object.keys(_module.getters ?? {}).reduce((acc, key) => ({
    ...acc,
    [key]: () => _module.getters?.[key](
      (
        typeof _module.state === 'function'
          ? (_module.state as (() => TState))()
          : _module.state
      ),
      _module.getters,
      store.state,
      store.getters,
    ),
  }), {} as TypedModule<TState, TModule>['getters']),
  /**
   * Access module mutations.
   * Mutations must be accessed as function calls to their respective keys.
   *
   * Ex: `someModule.mutations.setGreeting({ greeting: 'Hello' })`
  */
  mutations: Object.keys(_module.mutations!).reduce<TypedModule<TState, TModule>['mutations']>((acc, cur) => {
    const key = cur as keyof typeof _module.mutations;
    const originalFn = _module.mutations?.[key];
    if (!originalFn) { return { ...acc }; }
    const newFn = (payload: Parameters<typeof originalFn>[1]) => store.commit(`${moduleName}/${key}`, payload);
    return ({
      ...acc,
      [cur]: newFn,
    });
  }, {} as TypedModule<TState, TModule>['mutations']),
  /**
   * Access module actions.
   * Actions must be accessed as function calls to their respective keys.
   *
   * Actions may return a value contained within a Promise. All actions will return Promises (at least
   * Promise<void>) regardless of how they are defined in the origin module, thus all can be awaited.
   *
   * Ex: `await someModule.actions.showGreeting({ greeting: 'Hello' });`
  */
  actions: Object.keys(_module.actions!).reduce<TypedModule<TState, TModule>['actions']>((acc, cur) => {
    const key = cur as keyof typeof _module.actions;
    const originalVal = _module.actions?.[key];
    if (!originalVal) return { ...acc };
    const originalFn = 'handler' in originalVal ? originalVal.handler : originalVal;
    const newFn = (payload: Parameters<typeof originalFn>[1]) => store.dispatch(`${moduleName}/${key}`, payload);
    return ({
      ...acc,
      [cur]: newFn,
    });
  }, {} as TypedModule<TState, TModule>['actions']),
});
